/**
 * User API Service
 * Provides methods to interact with user endpoints
 */

import ormaApi from './OrmaApi';

/**
  * Fetch all users (excluding disabled groups)
 * @returns {Promise<{users: Array, count: number}>} Users data with count
 */
export const fetchUsers = async () => {
  try {
    const response = await ormaApi.get('/api/users');
    // ResponseHandler.success returns { users: [...], count: n } directly
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch organization viewers
 * Fetches all users system-wide that could have viewed the authenticated user's organizations' members.
 * Automatically determines which organizations to query based on the user's superUserAccess and workspaces.
 * @returns {Promise} API response with org viewers object
 */
export const fetchOrgViewers = async () => {
  try {
    const response = await ormaApi.get('/api/users/org-viewers');
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Find users by query
 * @param {Object} query - MongoDB query object
 * @returns {Promise} API response with users array
 */
export const findUsersByQuery = async (query = {}) => {
  try {
    const response = await ormaApi.post('/api/users/query', { query });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Find a user by ID
 * @param {string} userId - MongoDB ObjectId of the user
 * @returns {Promise} API response with user object
 */
export const findUserById = async (userId) => {
  try {
    const response = await ormaApi.get(`/api/users/${userId}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Update a user by ID
 * @param {string} userId - MongoDB ObjectId of the user
 * @param {Object} updates - Partial user object with fields to update
 * @returns {Promise} API response with updated user
 */
export const updateUser = async (userId, updates) => {
  try {
    const response = await ormaApi.put(`/api/users/${userId}`, updates);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Add a new user
 * @param {Object} userData - User data including email, name, namePublic, title, groupIds, role, etc.
 * @returns {Promise} API response
 */
export const addUser = async (userData) => {
  try {
    const response = await ormaApi.post('/api/users/addUser', userData);
    return response.data;
  } catch (error) {
    throw error;
  }
};
